import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { X, Clock } from "lucide-react";
import { useSubscription } from "@/hooks/subscription";
import { useInterval } from "@/hooks/use-interval";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import useTimerExpiration from "@/hooks/useTimerExpiration";

const TIMER_INITIAL_VALUE = 3 * 60; // 3 minutes in seconds
const TIMER_STORAGE_KEY = "specialDealTimerEnd";
const BANNER_SEEN_KEY = "trialExtensionBannerSeen";
const BANNER_DISPLAY_DELAY = 5000; // 5 seconds delay

const TrialExtensionBanner = () => {
  const [remainingTime, setRemainingTime] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { status, isSubscribed } = useSubscription();
  const navigate = useNavigate();
  const { isExpired } = useTimerExpiration();
  
  useEffect(() => {
    if (isSubscribed || status.onTrial || status.active) {
      setIsVisible(false);
      return;
    }
    
    const hasSeenBanner = localStorage.getItem(BANNER_SEEN_KEY);
    
    if (hasSeenBanner === "true") {
      setIsVisible(false);
      return;
    }

    const delayTimer = setTimeout(() => {
      if (!isSubscribed && !status.onTrial && !status.active) {
        setIsVisible(true);
      }
    }, BANNER_DISPLAY_DELAY);

    const timerEndStr = localStorage.getItem(TIMER_STORAGE_KEY);
    
    if (timerEndStr) {
      const timerEnd = parseInt(timerEndStr, 10);
      const now = Date.now();
      
      if (timerEnd <= now) {
        localStorage.setItem(BANNER_SEEN_KEY, "true");
        setIsVisible(false);
        return;
      }
      
      const remaining = Math.floor((timerEnd - now) / 1000);
      setRemainingTime(remaining > 0 ? remaining : 0);
    } else {
      const timerEnd = Date.now() + (TIMER_INITIAL_VALUE * 1000);
      localStorage.setItem(TIMER_STORAGE_KEY, timerEnd.toString());
      setRemainingTime(TIMER_INITIAL_VALUE);
    }

    return () => clearTimeout(delayTimer);
  }, [isSubscribed, status.onTrial, status.active]);
  
  const minutes = remainingTime ? Math.floor(remainingTime / 60) : 0;
  const seconds = remainingTime ? remainingTime % 60 : 0;
  
  useInterval(() => {
    if (remainingTime !== null && remainingTime > 0) {
      setRemainingTime(prev => (prev !== null ? prev - 1 : null));
      
      if (remainingTime === 1) {
        localStorage.setItem(BANNER_SEEN_KEY, "true");
      }
    }
  }, remainingTime !== null && remainingTime > 0 ? 1000 : null);
  
  const closeBanner = () => {
    setIsVisible(false);
    localStorage.setItem(BANNER_SEEN_KEY, "true");
  };
  
  const handleGetExtendedTrial = async () => {
    try {
      navigate("/pricing?extended=true");
      toast.success("Extended trial option selected!");
    } catch (error) {
      console.error("Failed to navigate:", error);
    }
  };
  
  const handleGetDiscount = async () => {
    try {
      navigate("/pricing?discount=20");
      toast.success("20% lifetime discount option selected!");
    } catch (error) {
      console.error("Failed to start checkout:", error);
    }
  };
  
  if (!isVisible || remainingTime === null || remainingTime <= 0 || isSubscribed || status.onTrial || status.active) {
    return null;
  }

  return (
    <motion.div 
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 20, opacity: 0 }}
      className="fixed bottom-0 left-0 right-0 z-50 p-4"
    >
      <div className="max-w-5xl mx-auto">
        <div className="bg-gradient-to-r from-indigo-600/30 to-purple-600/30 rounded-lg shadow-lg p-5 text-white relative 
          backdrop-blur-md border border-purple-500/20 
          hover:shadow-[0_0_15px_rgba(139,92,246,0.3)]
          transition-all duration-300"
        >
          <button 
            onClick={closeBanner} 
            className="absolute top-2 right-2 text-white hover:text-gray-200"
            aria-label="Close banner"
          >
            <X size={18} />
          </button>
          
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="flex items-center space-x-4 mb-3 md:mb-0">
              <div className="bg-white/20 rounded-full p-2">
                <Clock className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Spark Now or Never Deal 🧨</h3>
                <p className="text-sm md:text-base">
                  Special offer expires in: <span className="font-mono font-bold text-xl bg-white/20 px-2 py-1 rounded">{minutes}:{seconds.toString().padStart(2, '0')}</span>
                </p>
              </div>
            </div>
            
            <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-3 w-full md:w-auto">
              <Button 
                onClick={handleGetExtendedTrial}
                className="bg-white text-indigo-700 hover:bg-gray-100 font-medium px-4 py-2 h-auto"
                disabled={isExpired}
              >
                {isExpired ? "Offer Expired" : "Extend to 14-Day Trial"}
              </Button>
              
              <Button 
                onClick={handleGetDiscount}
                className="bg-indigo-500 text-white hover:bg-indigo-600 font-medium px-4 py-2 h-auto"
                disabled={isExpired}
              >
                {isExpired ? "Offer Expired" : "Pay Now Get 20% Off Lifetime Discount!"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TrialExtensionBanner;
